import {
  GRAFTCODE_ONBOARDING_PHASE,
  GRAFTCODE_ONBOARDING_WIZARD_STEP,
  resolveSetupModeForGoal,
} from '../graftcode-onboarding.const'
import {
  clearPersistedGraftcodeOnboardingState,
  loadPersistedGraftcodeOnboardingState,
  mergePersistedGraftcodeOnboardingState,
} from '../graftcode-onboarding.persistence'
import { resolveDefaultStackForScenario } from '../lib/buildOnboardingStackOptions'
import { detectGuideOsFromUserAgent } from '../lib/detectGuideOsFromUserAgent'
import type { GraftcodeOnboardingGuideOs } from '../lib/graftcode-onboarding-guide-os.const'
import { resolvePreferredStackForOnboardingScenario } from '../mappers/getPersonalizedOnboardingCourses'

import { graftcodeOnboardingInitialState } from './graftcode-onboarding.slice'
import type { GraftcodeOnboardingStore, GraftcodeOnboardingStoreActions } from './graftcode-onboarding.type'

type GraftcodeOnboardingSetState = (
  partial: Partial<GraftcodeOnboardingStore> | ((state: GraftcodeOnboardingStore) => Partial<GraftcodeOnboardingStore>),
  replace?: false,
  actionName?: string
) => void

type GraftcodeOnboardingGetState = () => GraftcodeOnboardingStore

type SetAndPersist = (
  partial: Partial<GraftcodeOnboardingStore> | ((state: GraftcodeOnboardingStore) => Partial<GraftcodeOnboardingStore>),
  actionName: string
) => void

export function createGraftcodeOnboardingActions(
  set: GraftcodeOnboardingSetState,
  get: GraftcodeOnboardingGetState,
  setAndPersist: SetAndPersist
): GraftcodeOnboardingStoreActions {
  return {
    hydrate: () => {
      const persisted = loadPersistedGraftcodeOnboardingState()
      set(mergePersistedGraftcodeOnboardingState(persisted), false, 'graftcodeOnboarding/hydrate')
    },
    openWizard: () =>
      set(
        {
          phase: GRAFTCODE_ONBOARDING_PHASE.wizard,
          wizardStep: GRAFTCODE_ONBOARDING_WIZARD_STEP.goal,
        },
        false,
        'graftcodeOnboarding/openWizard'
      ),
    reset: () => {
      clearPersistedGraftcodeOnboardingState()
      set(
        {
          ...graftcodeOnboardingInitialState,
          guideOs: detectGuideOsFromUserAgent(),
          hydrated: true,
        },
        false,
        'graftcodeOnboarding/reset'
      )
    },
    setGoal: (goalId) => {
      setAndPersist(
        {
          goalId,
          setupMode: resolveSetupModeForGoal(goalId),
        },
        'graftcodeOnboarding/setGoal'
      )
    },
    goToScenarioStep: () =>
      set({ wizardStep: GRAFTCODE_ONBOARDING_WIZARD_STEP.scenario }, false, 'graftcodeOnboarding/goToScenarioStep'),
    goToGoalStep: () =>
      set({ wizardStep: GRAFTCODE_ONBOARDING_WIZARD_STEP.goal }, false, 'graftcodeOnboarding/goToGoalStep'),
    completeWizard: (scenarioId) => {
      const { goalId, guideOsManual, primaryScenarioId, scenarioId: previousScenarioId } = get()
      const scenarioChanged = previousScenarioId !== scenarioId
      setAndPersist(
        {
          scenarioId,
          primaryScenarioId: primaryScenarioId ?? scenarioId,
          selectedStackId: resolveDefaultStackForScenario(scenarioId, resolveSetupModeForGoal(goalId)),
          wizardCompleted: true,
          phase: GRAFTCODE_ONBOARDING_PHASE.idle,
          setupMode: resolveSetupModeForGoal(goalId),
          ...(scenarioChanged
            ? {
                activeStepIndex: 0,
                tutorialCompleted: false,
              }
            : {}),
          ...(guideOsManual ? {} : { guideOs: detectGuideOsFromUserAgent() }),
        },
        'graftcodeOnboarding/completeWizard'
      )
    },
    setSelectedStackId: (stackId) => {
      setAndPersist(
        {
          selectedStackId: stackId,
          activeStepIndex: 0,
          tutorialVideoHidden: false,
          tutorialVideoPlaying: false,
        },
        'graftcodeOnboarding/setSelectedStackId'
      )
    },
    setSetupMode: (mode) => {
      setAndPersist(
        {
          setupMode: mode,
          activeStepIndex: 0,
          tutorialVideoHidden: false,
          tutorialVideoPlaying: false,
        },
        'graftcodeOnboarding/setSetupMode'
      )
    },
    setGuideOs: (os: GraftcodeOnboardingGuideOs) => {
      setAndPersist({ guideOs: os, guideOsManual: true }, 'graftcodeOnboarding/setGuideOs')
    },
    syncGuideOsFromUserAgent: () => {
      const { guideOsManual } = get()
      if (guideOsManual) {
        return
      }
      setAndPersist({ guideOs: detectGuideOsFromUserAgent() }, 'graftcodeOnboarding/syncGuideOsFromUserAgent')
    },
    setActiveStepIndex: (index) => {
      setAndPersist(
        {
          activeStepIndex: index,
          tutorialVideoPlaying: false,
          ...(index < 1 ? { tutorialVideoHidden: false } : {}),
        },
        'graftcodeOnboarding/setActiveStepIndex'
      )
    },
    completeTutorial: () => {
      const { scenarioId, selectedStackId, completedScenarioIds, completedStacksByScenario } = get()
      const nextCompleted =
        scenarioId && !completedScenarioIds.includes(scenarioId)
          ? [...completedScenarioIds, scenarioId]
          : completedScenarioIds
      const nextStacksByScenario =
        scenarioId && selectedStackId
          ? { ...completedStacksByScenario, [scenarioId]: selectedStackId }
          : completedStacksByScenario
      setAndPersist(
        {
          tutorialCompleted: true,
          tutorialVideoPlaying: false,
          completedScenarioIds: nextCompleted,
          lastCompletedStackId: selectedStackId ?? null,
          completedStacksByScenario: nextStacksByScenario,
        },
        'graftcodeOnboarding/completeTutorial'
      )
    },
    dismissTutorialVideo: () => {
      setAndPersist(
        { tutorialVideoHidden: true, tutorialVideoPlaying: false },
        'graftcodeOnboarding/dismissTutorialVideo'
      )
    },
    reopenTutorialVideo: () => {
      setAndPersist(
        { tutorialVideoHidden: false, tutorialVideoPlaying: true },
        'graftcodeOnboarding/reopenTutorialVideo'
      )
    },
    setTutorialVideoPlaying: (playing) => {
      set({ tutorialVideoPlaying: playing }, false, 'graftcodeOnboarding/setTutorialVideoPlaying')
    },
    reopenWizardFromDashboard: () => {
      const { goalId } = get()
      setAndPersist(
        {
          phase: GRAFTCODE_ONBOARDING_PHASE.wizard,
          wizardStep: goalId ? GRAFTCODE_ONBOARDING_WIZARD_STEP.scenario : GRAFTCODE_ONBOARDING_WIZARD_STEP.goal,
          tutorialVideoPlaying: false,
        },
        'graftcodeOnboarding/reopenWizardFromDashboard'
      )
    },
    resumeGuideForScenario: (scenarioId) => {
      const { setupMode, lastCompletedStackId, completedStacksByScenario } = get()
      setAndPersist(
        {
          scenarioId,
          selectedStackId: resolvePreferredStackForOnboardingScenario(
            scenarioId,
            setupMode,
            lastCompletedStackId,
            completedStacksByScenario
          ),
          activeStepIndex: 0,
          tutorialCompleted: false,
          tutorialVideoHidden: false,
          tutorialVideoPlaying: false,
        },
        'graftcodeOnboarding/resumeGuideForScenario'
      )
    },
  }
}
